import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import i18 from 'i18next';
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Col,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';

import CollapsibleCard from '../common/CollapsibleCard';
import InputWithAddon from '../common/InputWithAddon';
import LocationSearchInput from '../orders/LocationSearchInput';
import Restaurant from 'utils/restaurant';
import { logError } from 'utils/log';
import { currency } from 'globals/currency';
import { ModalTypes, isStageHost, isLocalHost } from 'config';
import { OTO_PRODUCTS, EDriverType, EPaymentType } from 'enums';
import withSetModal, { TSetModalFunction } from '../../containers/WithSetModal';
import { driverTypeByProduct } from 'utils/deliveries';
import {
  IDelivery,
  IDeliveryCreateData,
  TDeliveryEstimate,
} from 'types/delivery';
import { NormalizedRestaurant } from 'types/restaurants';
import Shapes from 'shapes/main';

interface IAddress {
  city: string;
  street: string;
  homeNumber: string;
  flatNumber?: string | null;
  lng: number | string;
  lat: number | string;
}
const DEFAULT_ADDRESS: IAddress = {
  city: '',
  street: '',
  homeNumber: '',
  flatNumber: '',
  lng: '',
  lat: '',
};

const testAddresses: IAddress[] = [
  {
    street: 'Cicha',
    homeNumber: '3',
    lng: 22.5552004,
    lat: 51.249833,
    city: 'Lublin',
  },
  {
    street: 'Bolesława Śmiałego',
    homeNumber: '8',
    lng: 22.5369216,
    lat: 51.2395188,
    city: 'Lublin',
  },
  {
    street: 'Ludowa',
    homeNumber: '15',
    lng: 22.5847371,
    lat: 51.2636578,
    city: 'Lublin',
  },
  {
    street: 'Chodźki',
    homeNumber: '27',
    lng: 22.5625304,
    lat: 51.2650119,
    city: 'Lublin',
  },
  {
    street: 'Szmaragdowa',
    homeNumber: '46',
    lng: 22.5131651,
    lat: 51.2271064,
    city: 'Lublin',
  },
];

interface ICallForCourrierProps {
  addDelivery: (
    data: IDeliveryCreateData,
    restaurant: NormalizedRestaurant
  ) => Promise<IDelivery>;
  isAdmin: boolean;
  restaurant: NormalizedRestaurant;
  setModal: TSetModalFunction;
}

const CallForCourrierForm: React.FC<ICallForCourrierProps> = (props) => {
  const { addDelivery, isAdmin, restaurant } = props;

  const availableDriverTypes = useMemo(
    () =>
      Object.entries(driverTypeByProduct)
        .filter(([product, driverType]) =>
          Restaurant.hasProduct(restaurant, product)
        )
        .map(([product, driverType]) => driverType),
    [restaurant]
  );

  const [address, setAddress] = useState(DEFAULT_ADDRESS);
  const [estimate, setEstimate] = useState<TDeliveryEstimate | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [phone, setPhone] = useState('');
  const [payment, setPayment] = useState(EPaymentType.CARD_ON_PLACE);
  const [price, setPrice] = useState<number | undefined>(undefined);
  const [driverType, setDriverType] = useState(availableDriverTypes[0]);
  const [notes, setNotes] = useState('');

  if (!Restaurant.canOrderDelivery(restaurant)) {
    return null;
  }

  const resetToDefault = () => {
    setAddress(DEFAULT_ADDRESS);
    setEstimate(null);
    setPhone('');
    setPrice(undefined);
    setNotes('');
  };

  const errorClassName = 'text-danger mb-2';

  const isEmptyPriceError = !price && payment !== EPaymentType.PAID;
  const isDriverTypeError =
    driverType === EDriverType.Robot && payment !== EPaymentType.PAID;
  const isCityError = !address.city || !address.city.trim();
  const isCoordinatesError = !address.lat || !address.lng;
  const isEstimateMissingError = !estimate;
  const isNoPricingError = !!estimate && !estimate.hasPricing;

  const paymentLabelByType: Record<string, string> = {
    [EPaymentType.CASH]: i18.t('call-for-courrier-form.payment-cash'),
    [EPaymentType.CARD_ON_PLACE]: i18.t('call-for-courrier-form.payment-card'),
    [EPaymentType.PAID]: i18.t('call-for-courrier-form.payment-paid'),
  };

  const submitDelivery = (payload: IDeliveryCreateData) =>
    addDelivery(payload, restaurant)
      .then((newDelivery: IDelivery) => {
        toast.success(i18.t('call-for-courrier-form.courier-called'));
        resetToDefault();
      })
      .catch((e) => {
        logError('call for courrier error', e);
        const isNoPricingApiError = !!e?.errors?.distance;
        toast.error(
          i18.t(
            isNoPricingApiError
              ? 'call-for-courrier-form.error-api-no-pricing'
              : 'call-for-courrier-form.error-calling-courier'
          )
        );
      });

  const showConfirmationModal = ({
    addressStr,
    deliveryEstimate,
    informBeforeTime,
    deliveryAtTime,
  }: {
    addressStr: string;
    deliveryEstimate: TDeliveryEstimate;
    informBeforeTime: string;
    deliveryAtTime?: string;
  }) => {
    const payload: IDeliveryCreateData = {
      type: 'external',
      driver_type: driverType,
      ...(deliveryAtTime
        ? { delivery_at: deliveryAtTime }
        : { inform_before: parseInt(informBeforeTime) }),
      address: addressStr,
      price,
      payment_status: payment,
      lng: address.lng,
      lat: address.lat,
      phone,
      notes,
    };

    const summaryRow = (label: string, value: React.ReactNode) => (
      <div className="d-flex justify-content-between">
        <span className="mr-2">{label}:</span>
        <strong className="text-right">{value}</strong>
      </div>
    );

    props.setModal(
      {
        title: i18.t('call-for-courrier-form.confirm-title'),
        text: (
          <>
            {summaryRow(i18.t('call-for-courrier-form.confirm-address'), addressStr)}
            {summaryRow(
              i18.t('call-for-courrier-form.confirm-distance'),
              `${deliveryEstimate.distance} m`
            )}
            {summaryRow(
              i18.t('call-for-courrier-form.confirm-driver-type'),
              i18.t(`delivery.driver-types.${driverType}`)
            )}
            {summaryRow(
              i18.t('call-for-courrier-form.confirm-payment'),
              paymentLabelByType[payment] || payment
            )}
            {payment !== EPaymentType.PAID &&
              summaryRow(
                i18.t('call-for-courrier-form.confirm-price'),
                `${price} ${currency}`
              )}
            {deliveryEstimate.restaurantCost !== null &&
              summaryRow(
                i18.t('call-for-courrier-form.confirm-delivery-cost'),
                `${deliveryEstimate.restaurantCost} ${currency}`
              )}
            {deliveryAtTime
              ? summaryRow(
                  i18.t('call-for-courrier-form.confirm-delivery-at'),
                  deliveryAtTime
                )
              : summaryRow(
                  i18.t('call-for-courrier-form.confirm-time'),
                  `${parseInt(informBeforeTime)} min`
                )}
          </>
        ),
        confirm: () => submitDelivery(payload),
        confirmColor: 'success',
        confirmText: i18.t('call-for-courrier-form.confirm-button'),
        cancelText: i18.t('Cancel'),
      },
      ModalTypes.CONFIRM
    );
  };

  const handleCallCourrierClick = () => {
    if (
      !address.street ||
      !address.homeNumber ||
      isCityError ||
      isCoordinatesError ||
      isEstimateMissingError ||
      isNoPricingError ||
      !phone ||
      isEmptyPriceError ||
      isDriverTypeError
    ) {
      setShowValidation(true);
      return null;
    } else if (showValidation) {
      setShowValidation(false);
    }

    const flatPart = address.flatNumber ? `/${address.flatNumber}` : '';
    const addressStr = `${address.city.trim()}, ${address.street.trim()} ${address.homeNumber.trim()}${flatPart}`;
    const deliveryEstimate = estimate as TDeliveryEstimate;

    props.setModal(
      {
        ...getCommonCallForCourrierProps({
          isFoodeli: restaurant.products.includes(
            OTO_PRODUCTS.foodeli_deliveries
          ),
          isRobot: driverType === EDriverType.Robot,
        }),
        // @ts-ignore fix types, this should be working
        confirm: (informBeforeTime: string, deliveryAtTime?: string) => {
          if (deliveryAtTime && !/^\d{2}:\d{2}$/.test(deliveryAtTime)) {
            toast.error(
              i18.t('call-for-courrier-form.error-time-format')
            );
            return;
          }
          // PageModal closes the current modal right after confirm() - open the summary in the next tick
          setTimeout(
            () =>
              showConfirmationModal({
                addressStr,
                deliveryEstimate,
                informBeforeTime,
                deliveryAtTime,
              }),
            0
          );
        },
      },
      ModalTypes.SELECT
    );
  };

  return (
    <CollapsibleCard
      title={i18.t('delivery.call-driver.call-external-delivery')}
    >
      <CardBody className="row">
        {(isAdmin || isStageHost() || isLocalHost()) && (
          <Col xs="12" className="row mx-0 align-items-start">
            <p className="text-left w-100">
              {i18.t('call-for-courrier-form.admin-test-address-hint')}
            </p>
            {testAddresses.map((addr) => (
              <Card
                key={addr.street}
                tag="button"
                className="mw-250 p-2 text-center mr-2"
                onClick={() => setAddress({ ...addr, flatNumber: '' })}
              >
                {addr.street} {addr.homeNumber}, {addr.city}
              </Card>
            ))}
          </Col>
        )}
        <Col xs="12" md="4">
          <FormGroup>
            <Label className="row">{i18.t('delivery.address')}</Label>
            <LocationSearchInput
              address={address}
              onAddressUpdate={setAddress}
              onEstimateUpdate={setEstimate}
              restaurant={restaurant}
            />
          </FormGroup>
          <FormGroup>
            <Label className="row">{i18.t('delivery.customer-phone')}</Label>
            <Row>
              <Input
                type="tel"
                placeholder="np. 791222123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Row>
          </FormGroup>
        </Col>
        <Col xs="12" md="4">
          <FormGroup>
            <Label>
              <Label>{i18.t('delivery.payment-type')}</Label>
            </Label>
            <Input
              type="select"
              value={payment}
              onChange={(e) => setPayment(e.target.value as EPaymentType)}
            >
              <option value={EPaymentType.CASH}>{i18.t('call-for-courrier-form.payment-cash')}</option>
              <option value={EPaymentType.CARD_ON_PLACE}>
                {i18.t('call-for-courrier-form.payment-card')}
              </option>
              <option value={EPaymentType.PAID}>{i18.t('call-for-courrier-form.payment-paid')}</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>{i18.t('delivery.price')}</Label>
            <InputWithAddon
              addonText={currency}
              id="price"
              name="price"
              type="number"
              value={price}
              onChange={(e) =>
                setPrice(
                  e.target.value ? parseFloat(e.target.value) : undefined
                )
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>{i18.t('delivery.driverType')}</Label>
            <Input
              addonText={currency}
              id="driverType"
              name="driverType"
              type="select"
              value={driverType}
              onChange={(e) => setDriverType(e.target.value as EDriverType)}
            >
              {availableDriverTypes.map((driverType) => (
                <option value={driverType} key={driverType}>
                  {i18.t(`delivery.driver-types.${driverType}`)}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col xs="12" md="4">
          <FormGroup>
            <Label>{i18.t('delivery.notes')}</Label>
            <Input
              type="textarea"
              value={notes}
              placeholder={i18.t('call-for-courrier-form.notes-placeholder')}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FormGroup>
          {showValidation && (
            <>
              {isCoordinatesError && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-coordinates')}
                </div>
              )}
              {!address.street && (
                <div className={errorClassName}>{i18.t('call-for-courrier-form.error-street')}</div>
              )}
              {isCityError && (
                <div className={errorClassName}>{i18.t('call-for-courrier-form.error-city')}</div>
              )}
              {!address.homeNumber && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-home-number')}
                </div>
              )}
              {!isCoordinatesError && isEstimateMissingError && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-estimate-missing')}
                </div>
              )}
              {isNoPricingError && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-no-pricing', {
                    distance: estimate?.distance,
                  })}
                </div>
              )}
              {!phone && (
                <div className={errorClassName}>{i18.t('call-for-courrier-form.error-phone')}</div>
              )}
              {isEmptyPriceError && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-price')}
                </div>
              )}
              {isDriverTypeError && (
                <div className={errorClassName}>
                  {i18.t('call-for-courrier-form.error-robot-payment')}
                </div>
              )}
            </>
          )}
          <Button
            type="button"
            className="mt-2"
            onClick={handleCallCourrierClick}
          >
            {i18.t('call-for-courrier-form.call-button')}
          </Button>
        </Col>
      </CardBody>
    </CollapsibleCard>
  );
};

export function getCommonCallForCourrierProps({
  isFoodeli,
  isRobot,
}: {
  isFoodeli: boolean;
  isRobot: boolean;
}) {
  const robotCourrierTimes = [
    i18.t('call-for-courrier-form.time-now'),
    '10 minut',
    '15 minut',
    '20 minut',
    '30 minut',
  ];

  const humanCourrierTimes = [
    '15 minut',
    '20 minut',
    '25 minut',
    '30 minut',
    '35 minut',
    '45 minut',
    '60 minut',
    '90 minut',
    i18.t('call-for-courrier-form.time-120-plus'),
  ];

  return {
    title: i18.t('delivery.call-driver.set-prepare-time-title'),
    text: i18.t(
      isFoodeli
        ? 'delivery.call-driver.set-prepare-time-desc--foodeli'
        : 'delivery.call-driver.set-prepare-time-desc'
    ),
    options: isRobot ? robotCourrierTimes : humanCourrierTimes,
    other: i18.t('delivery.call-driver.fixed-time-hint'),
    otherPlaceholder: 'np. 16:30',
    confirmText: i18.t(isRobot ? 'call-for-courrier-form.call-robot' : 'call-for-courrier-form.call-courier'),
    confirmColor: 'success' as const,
  };
}

CallForCourrierForm.propTypes = {
  addDelivery: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  restaurant: Shapes.restaurantShape.isRequired,
  setModal: PropTypes.func.isRequired,
};

export default withSetModal(CallForCourrierForm);
